import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ConnectButton } from '@rainbow-me/rainbowkit';
import { useAccount, useSwitchChain, useReadContract, useWriteContract, useWaitForTransactionReceipt, useBalance } from 'wagmi';
import { parseUnits, formatUnits, type Address, formatEther } from 'viem';
import { QRCodeSVG } from 'qrcode.react';
import { AppSidebar } from "@/components/app-sidebar";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/breadcrumb";
import { Separator } from "@/components/separator";
import {
  SidebarInset,
  SidebarProvider,
  SidebarTrigger,
} from "@/components/sidebar";
import { Button } from "@/components/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/card";
import { Input } from "@/components/input";
import {
  RiExchangeFundsLine,
  RiArrowLeftRightLine,
  RiAlertLine,
  RiLoader4Line,
  RiCheckLine,
  RiErrorWarningLine,
  RiHistoryLine,
  RiExternalLinkLine,
  RiTimeLine,
  RiFileCopyLine,
  RiGasStationLine
} from "@remixicon/react";
import { BRIDGE_NETWORKS, getCommonTokens, type BridgeNetwork, type BridgeToken } from "@/configs/bridge.config";
import { ERC20_ABI } from "@/abis/erc20";
import { BRIDGE_ABI } from "@/abis/bridge";
import { useBridge } from "@/providers/global-data.provider";

// Bridge transaction interface
interface BridgeTransaction {
  id: string;
  fromChainId: number;
  toChainId: number;
  fromToken: string;
  toToken: string;
  amount: string;
  sender: string;
  recipient: string;
  status: string;
  txHash: string;
  timestamp: number;
  fromChainName?: string;
  toChainName?: string;
  tokenSymbol?: string;
  destinationHash?: string;
}

export default function BridgePage() {
  const { address, isConnected, chain } = useAccount();
  const { switchChain } = useSwitchChain();

  // State management
  const [fromNetwork, setFromNetwork] = useState<BridgeNetwork>(BRIDGE_NETWORKS[0]); // Larissa
  const [toNetwork, setToNetwork] = useState<BridgeNetwork>(BRIDGE_NETWORKS[2]); // Ethereum
  const [selectedToken, setSelectedToken] = useState<BridgeToken | null>(null);
  const [amount, setAmount] = useState("");
  const [isApproving, setIsApproving] = useState(false);
  const [isBridging, setIsBridging] = useState(false);
  const [txHash, setTxHash] = useState<string>("");
  const [error, setError] = useState<string>("");

  // Get transaction history from global provider
  const { transactionHistory, isLoadingHistory, historyError, refetchHistory, processTransaction } = useBridge();
  
  // Helper to get chain ID from chain name
  const getChainIdFromName = (chainName: string): number => {
    const network = BRIDGE_NETWORKS.find(n => n.name === chainName);
    return network?.networkId || 0;
  };
  
  // Convert global transaction history to BridgeTransaction format
  const transactions: BridgeTransaction[] = transactionHistory.map(tx => ({
    id: tx.id,
    fromChainId: getChainIdFromName(tx.sourceChain),
    toChainId: getChainIdFromName(tx.destinationChain),
    fromToken: tx.token,
    toToken: tx.token,
    amount: tx.amount,
    sender: address || '',
    recipient: address || '',
    status: tx.status,
    txHash: tx.sourceHash || tx.id,
    timestamp: tx.timestamp,
    fromChainName: tx.sourceChain,
    toChainName: tx.destinationChain,
    tokenSymbol: tx.token,
    destinationHash: tx.destinationHash,
  }));

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const transactionsPerPage = 3;

  // Fee validation state
  const [showRelayFeeWarning, setShowRelayFeeWarning] = useState(false);
  const [relayFeeError, setRelayFeeError] = useState<string>("");
  const [userFeeError, setUserFeeError] = useState<string>("");
  const [proceedDespiteWarning, setProceedDespiteWarning] = useState(false);

  // Process transaction state
  const [processTxHash, setProcessTxHash] = useState<string>("");
  const [processChainId, setProcessChainId] = useState<number | undefined>(undefined);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processError, setProcessError] = useState<string>("");
  const [processSuccess, setProcessSuccess] = useState<{
    chainId: number;
    chainName: string;
    transactionHash: string;
  } | null>(null);

  // Get available tokens for selected networks
  const availableTokens = getCommonTokens(fromNetwork.networkId, toNetwork.networkId);

  // Use global provider's refetch function
  const fetchTransactionHistory = () => {
    refetchHistory();
    setCurrentPage(1);
  };

  // Set default token when networks change
  useEffect(() => {
    if (availableTokens.length > 0) {
      setSelectedToken(availableTokens[0]);
    } else {
      setSelectedToken(null);
    }
    setAmount("");
    setError("");
  }, [fromNetwork, toNetwork]);

  // Reset to first page when transactions change
  useEffect(() => {
    setCurrentPage(1);
  }, [transactionHistory]);

  // Get token balance - read from the source network (fromNetwork)
  const { data: tokenBalance, refetch: refetchBalance } = useReadContract({
    address: selectedToken?.address as Address,
    abi: ERC20_ABI,
    functionName: 'balanceOf',
    args: address ? [address] : undefined,
    chainId: fromNetwork.networkId, // Specify the chain to read from
    query: {
      enabled: !!address && !!selectedToken && !!fromNetwork,
    }
  });

  // Get token allowance - read from the source network (fromNetwork)
  const { data: tokenAllowance, refetch: refetchAllowance } = useReadContract({
    address: selectedToken?.address as Address,
    abi: ERC20_ABI,
    functionName: 'allowance',
    args: address && fromNetwork ? [address, fromNetwork.erc20Handler as Address] : undefined,
    chainId: fromNetwork.networkId, // Specify the chain to read from
    query: {
      enabled: !!address && !!selectedToken && !!fromNetwork,
    }
  });

  // Get user's native token balance on source chain (for gas fees)
  const { data: userNativeBalance } = useBalance({
    address: address,
    chainId: fromNetwork.networkId,
    query: {
      enabled: !!address && !!fromNetwork,
    }
  });

  // Get relay address native token balance on destination chain
  const { data: relayNativeBalance } = useBalance({
    address: toNetwork.relay as Address,
    chainId: toNetwork.networkId,
    query: {
      enabled: !!toNetwork,
    }
  });



  // Contract write hooks
  const { writeContract: approveToken, data: approveData } = useWriteContract();
  const { writeContract: depositBridge, data: depositData } = useWriteContract();

  // Wait for approval transaction
  const { isLoading: isApprovePending, isSuccess: isApproveSuccess } = useWaitForTransactionReceipt({
    hash: approveData,
  });

  // Wait for bridge transaction
  const { isLoading: isBridgePending, isSuccess: isBridgeSuccess } = useWaitForTransactionReceipt({
    hash: depositData,
  });

  useEffect(() => {
    if (isApproveSuccess) {
      setIsApproving(false);
      // Refetch allowance after approval succeeds
      setTimeout(() => {
        refetchAllowance();
      }, 1000); // Wait 1 second for blockchain to update
    }
  }, [isApproveSuccess, refetchAllowance]);

  useEffect(() => {
    if (isBridgeSuccess && depositData) {
      setIsBridging(false);
      setTxHash(depositData);
      setAmount("");
      // Refetch balance, allowance, and transaction history after bridge succeeds
      setTimeout(() => {
        refetchBalance();
        refetchAllowance();
        refetchHistory();
      }, 1000);
    }
  }, [isBridgeSuccess, depositData, refetchBalance, refetchAllowance]);

  // Helper functions
  const switchNetworks = () => {
    const temp = fromNetwork;
    setFromNetwork(toNetwork);
    setToNetwork(temp);
    setAmount("");
    setError("");
  };

  const needsApproval = () => {
    if (!amount || !selectedToken) return false;
    try {
      const amountBigInt = parseUnits(amount, selectedToken.decimals);
      const allowance = tokenAllowance as bigint || BigInt(0);
      return allowance < amountBigInt;
    } catch {
      return false;
    }
  };

  // Fee validation function
  const validateFees = (): { userFeeValid: boolean; relayFeeValid: boolean } => {
    setUserFeeError("");
    setRelayFeeError("");

    // Estimate gas cost with 20% buffer for safety
    const gasBuffer = 1.2;

    // 1. Check user's native token balance for gas on source chain
    if (userNativeBalance) {
      const estimatedGasUnits = BigInt(fromNetwork.gasNeed);
      // Estimate gas price at 50 gwei (conservative estimate)
      const estimatedGasPrice = parseUnits("50", 9); // 50 gwei
      const estimatedGasCost = estimatedGasUnits * estimatedGasPrice;
      const estimatedGasCostWithBuffer = (estimatedGasCost * BigInt(Math.floor(gasBuffer * 100))) / BigInt(100);

      if (userNativeBalance.value < estimatedGasCostWithBuffer) {
        const required = formatEther(estimatedGasCostWithBuffer);
        const current = formatEther(userNativeBalance.value);
        const shortfall = formatEther(estimatedGasCostWithBuffer - userNativeBalance.value);

        setUserFeeError(
          `Insufficient ${fromNetwork.nativeTokenSymbol} for gas fees. ` +
          `Required: ${parseFloat(required).toFixed(6)} ${fromNetwork.nativeTokenSymbol}, ` +
          `Current: ${parseFloat(current).toFixed(6)} ${fromNetwork.nativeTokenSymbol}, ` +
          `Need ${parseFloat(shortfall).toFixed(6)} more ${fromNetwork.nativeTokenSymbol}`
        );
        return { userFeeValid: false, relayFeeValid: false };
      }
    }

    // 2. Check relay address balance on destination chain
    if (relayNativeBalance) {
      const estimatedGasUnits = BigInt(toNetwork.gasNeed);
      const estimatedGasPrice = parseUnits("50", 9); // 50 gwei
      const estimatedGasCost = estimatedGasUnits * estimatedGasPrice;
      const estimatedGasCostWithBuffer = (estimatedGasCost * BigInt(Math.floor(gasBuffer * 100))) / BigInt(100);

      if (relayNativeBalance.value < estimatedGasCostWithBuffer) {
        const required = formatEther(estimatedGasCostWithBuffer);
        const current = formatEther(relayNativeBalance.value);

        setRelayFeeError(
          `The relay address on ${toNetwork.name} has insufficient ${toNetwork.nativeTokenSymbol} ` +
          `to complete the bridge transaction. ` +
          `Required: ~${parseFloat(required).toFixed(6)} ${toNetwork.nativeTokenSymbol}, ` +
          `Current: ${parseFloat(current).toFixed(6)} ${toNetwork.nativeTokenSymbol}`
        );
        return { userFeeValid: true, relayFeeValid: false };
      }
    }

    return { userFeeValid: true, relayFeeValid: true };
  };

  const handleApprove = async () => {
    if (!selectedToken || !amount) return;

    setError("");
    setUserFeeError("");
    setIsApproving(true);

    try {
      // Validate user's gas fees
      const { userFeeValid } = validateFees();
      if (!userFeeValid) {
        setIsApproving(false);
        return;
      }

      // Check if on correct network
      if (chain?.id !== fromNetwork.networkId) {
        switchChain({ chainId: fromNetwork.networkId });
      }

      const amountBigInt = parseUnits(amount, selectedToken.decimals);

      approveToken({
        address: selectedToken.address as Address,
        abi: ERC20_ABI,
        functionName: 'approve',
        args: [fromNetwork.erc20Handler as Address, amountBigInt],
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Approval failed");
      setIsApproving(false);
    }
  };

  const handleBridge = async () => {
    if (!selectedToken || !amount || !address) return;

    // Double-check approval before bridging
    if (needsApproval()) {
      setError("Please approve the token first before bridging");
      setIsBridging(false);
      return;
    }

    setError("");
    setUserFeeError("");
    setRelayFeeError("");
    setIsBridging(true);

    try {
      // Validate fees
      const { userFeeValid, relayFeeValid } = validateFees();

      if (!userFeeValid) {
        setIsBridging(false);
        return;
      }

      if (!relayFeeValid) {
        // Show relay fee warning modal
        setShowRelayFeeWarning(true);
        setIsBridging(false);

        // Don't proceed unless user confirms
        if (!proceedDespiteWarning) {
          return;
        }
      }

      // Check if on correct network
      if (chain?.id !== fromNetwork.networkId) {
        switchChain({ chainId: fromNetwork.networkId });
      }

      const amountBigInt = parseUnits(amount, selectedToken.decimals);

      // Convert recipient address to bytes without 0x prefix
      const recipientBytes = address.slice(2).padStart(40, '0');

      // Manually construct the data in the expected format
      const data =
        // amount (uint256)
        amountBigInt.toString(16).padStart(64, '0') +
        // recipient length (uint256) - 20 bytes for an Ethereum address
        BigInt(20).toString(16).padStart(64, '0') +
        // recipient address as bytes
        recipientBytes;

      // Add 0x prefix to the final data
      const depositData = `0x${data}`;

      depositBridge({
        address: fromNetwork.bridge as Address,
        abi: BRIDGE_ABI,
        functionName: 'deposit',
        args: [
          toNetwork.id, // destination chain ID
          selectedToken.resourceId as `0x${string}`, // resource ID
          depositData as `0x${string}`, // encoded data
        ],
      });

      // Reset warning state after successful submission
      setProceedDespiteWarning(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Bridge transaction failed");
      setIsBridging(false);
    }
  };

  const formatBalance = (balance: bigint | undefined, decimals: number) => {
    if (!balance) return "0";
    return parseFloat(formatUnits(balance, decimals)).toFixed(6);
  };

  const isValidAmount = () => {
    if (!amount || !selectedToken || !tokenBalance) return false;
    try {
      const amountBigInt = parseUnits(amount, selectedToken.decimals);
      return amountBigInt > BigInt(0) && amountBigInt <= (tokenBalance as bigint);
    } catch {
      return false;
    }
  };

  const formatTimestamp = (timestamp: number) => {
    if (!timestamp) return 'N/A';
    const date = new Date(timestamp * 1000);
    return date.toLocaleString();
  };

  const getStatusColor = (status: string | undefined) => {
    if (!status) return 'text-muted-foreground';

    switch (status.toLowerCase()) {
      case 'completed':
      case 'success':
        return 'text-green-500';
      case 'pending':
        return 'text-yellow-500';
      case 'failed':
      case 'error':
        return 'text-red-500';
      default:
        return 'text-muted-foreground';
    }
  };

  const getStatusIcon = (status: string | undefined) => {
    if (!status) return <RiHistoryLine className="size-4" />;

    switch (status.toLowerCase()) {
      case 'completed':
      case 'success':
        return <RiCheckLine className="size-4" />;
      case 'pending':
        return <RiTimeLine className="size-4" />;
      case 'failed':
      case 'error':
        return <RiErrorWarningLine className="size-4" />;
      default:
        return <RiHistoryLine className="size-4" />;
    }
  };

  const getExplorerUrl = (chainId: number, txHash: string) => {
    const network = BRIDGE_NETWORKS.find(n => n.id === chainId);
    return network ? `${network.scanURL}/tx/${txHash}` : '#';
  };

  // Handle process transaction
  const handleProcessTransaction = async () => {
    if (!processTxHash.trim()) {
      setProcessError("Please enter a transaction hash");
      return;
    }

    // Validate transaction hash format
    if (!processTxHash.startsWith('0x') || processTxHash.length !== 66) {
      setProcessError("Invalid transaction hash format. Must start with 0x and be 66 characters long.");
      return;
    }

    setIsProcessing(true);
    setProcessError("");
    setProcessSuccess(null);

    try {
      const result = await processTransaction(processTxHash.trim(), processChainId);

      if (result.status && result.data) {
        setProcessSuccess({
          chainId: result.data.chainId,
          chainName: result.data.chainName,
          transactionHash: result.data.transactionHash,
        });
        setProcessTxHash("");
        setProcessChainId(undefined);
        // Refresh transaction history after successful processing
        setTimeout(() => {
          refetchHistory();
        }, 1000);
      } else {
        setProcessError(result.message || "Failed to process transaction");
      }
    } catch (err) {
      setProcessError(err instanceof Error ? err.message : "An error occurred while processing the transaction");
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <SidebarProvider>
      <AppSidebar />
      <SidebarInset>
        <div className="px-4 md:px-6 lg:px-8 @container">
          <div className="w-full max-w-6xl mx-auto">
            <header className="flex flex-wrap gap-3 min-h-20 py-4 shrink-0 items-center transition-all ease-linear border-b">
              <div className="flex flex-1 items-center gap-2">
                <SidebarTrigger className="-ms-1" />
                <div className="max-lg:hidden lg:contents">
                  <Separator
                    orientation="vertical"
                    className="me-2 data-[orientation=vertical]:h-4"
                  />
                  <Breadcrumb>
                    <BreadcrumbList>
                      <BreadcrumbItem className="hidden md:block">
                        <BreadcrumbLink asChild>
                          <Link to="/">Home</Link>
                        </BreadcrumbLink>
                      </BreadcrumbItem>
                      <BreadcrumbSeparator className="hidden md:block" />
                      <BreadcrumbItem>
                        <BreadcrumbLink asChild>
                          <Link to="/blockchain">Blockchain</Link>
                        </BreadcrumbLink>
                      </BreadcrumbItem>
                      <BreadcrumbSeparator className="hidden md:block" />
                      <BreadcrumbItem>
                        <BreadcrumbPage>Bridge</BreadcrumbPage>
                      </BreadcrumbItem>
                    </BreadcrumbList>
                  </Breadcrumb>
                </div>
              </div>
              <ConnectButton />
            </header>

            <div className="py-6">
              <div className="flex items-center mb-6">
                <div className="w-1 h-8 bg-[#FF8A3C] mr-3"></div>
                <h1 className="text-3xl font-bold">Bridge</h1>
              </div>

              <div className="w-full max-w-xs h-1 bg-gradient-to-r from-[#FF8A3C] to-transparent mb-8 rounded-full"></div>

              {!isConnected && (
                <Card className="bg-yellow-500/10 border border-yellow-500/20 mb-6">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <RiAlertLine className="size-6 text-yellow-500 flex-shrink-0 mt-0.5" />
                      <div>
                        <h3 className="text-lg font-semibold text-yellow-500 mb-2">Wallet Connection Required</h3>
                        <p className="text-sm text-yellow-500/80">
                          Please connect your wallet to use the bridge. You need a connected wallet to transfer tokens between networks.
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Success Message */}
              {txHash && (
                <Card className="bg-green-500/10 border border-green-500/20 mb-6">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <RiCheckLine className="size-6 text-green-500 flex-shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-green-500 mb-2">Bridge Transaction Submitted!</h3>
                        <p className="text-sm text-green-500/80 mb-2">
                          Your bridge transaction has been submitted successfully. It will be processed in approximately {fromNetwork.blockConfirmations} block confirmations.
                        </p>
                        <a
                          href={`${fromNetwork.scanURL}/tx/${txHash}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-[#FF8A3C] hover:underline"
                        >
                          View on Explorer →
                        </a>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Error Message */}
              {error && (
                <Card className="bg-red-500/10 border border-red-500/20 mb-6">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <RiErrorWarningLine className="size-6 text-red-500 flex-shrink-0 mt-0.5" />
                      <div>
                        <h3 className="text-lg font-semibold text-red-500 mb-2">Transaction Failed</h3>
                        <p className="text-sm text-red-500/80">{error}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* User Fee Error Message */}
              {userFeeError && (
                <Card className="bg-red-500/10 border border-red-500/20 mb-6">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <RiGasStationLine className="size-6 text-red-500 flex-shrink-0 mt-0.5" />
                      <div>
                        <h3 className="text-lg font-semibold text-red-500 mb-2">Insufficient Gas Fees</h3>
                        <p className="text-sm text-red-500/80">{userFeeError}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Process Transaction Card */}
              <Card className="bg-sidebar border border-border mb-6">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <RiHistoryLine className="size-5 text-[#FF8A3C]" />
                    Process Deposit Transaction
                  </CardTitle>
                  <CardDescription>
                    If you've made a deposit transaction but it's not showing in your history, you can process it manually by entering the transaction hash.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex flex-col md:flex-row gap-4">
                      <div className="flex-1">
                        <label className="text-sm text-muted-foreground mb-2 block">Transaction Hash</label>
                        <Input
                          type="text"
                          placeholder="0x..."
                          value={processTxHash}
                          onChange={(e) => {
                            setProcessTxHash(e.target.value);
                            setProcessError("");
                            setProcessSuccess(null);
                          }}
                          className="font-mono"
                          disabled={isProcessing}
                        />
                      </div>
                      <div className="flex flex-col gap-2 min-w-[200px]">
                        <label className="text-sm text-muted-foreground">Chain (Optional)</label>
                        <div className="relative">
                          <select
                            value={processChainId || ""}
                            onChange={(e) => {
                              const value = e.target.value;
                              setProcessChainId(value ? parseInt(value) : undefined);
                            }}
                            className="w-full h-10 rounded-md border border-input bg-background px-3 py-2 text-sm appearance-none cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
                            disabled={isProcessing}
                          >
                            <option value="">Auto-detect</option>
                            {BRIDGE_NETWORKS.map((network) => (
                              <option key={network.id} value={network.networkId}>
                                {network.name}
                              </option>
                            ))}
                          </select>
                          <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-end">
                        <Button
                          onClick={handleProcessTransaction}
                          disabled={isProcessing || !processTxHash.trim()}
                          className="bg-[#FF8A3C] text-white hover:bg-[#FF8A3C]/90 h-10"
                        >
                          {isProcessing ? (
                            <>
                              <RiLoader4Line className="size-4 mr-2 animate-spin" />
                              Processing...
                            </>
                          ) : (
                            <>
                              <RiCheckLine className="size-4 mr-2" />
                              Process
                            </>
                          )}
                        </Button>
                      </div>
                    </div>

                    {/* Process Success Message */}
                    {processSuccess && (
                      <Card className="bg-green-500/10 border border-green-500/20">
                        <CardContent className="p-4">
                          <div className="flex items-start gap-3">
                            <RiCheckLine className="size-5 text-green-500 flex-shrink-0 mt-0.5" />
                            <div className="flex-1">
                              <h4 className="text-sm font-semibold text-green-500 mb-1">Transaction Processed Successfully!</h4>
                              <p className="text-xs text-green-500/80 mb-2">
                                Deposit record created for transaction on {processSuccess.chainName}
                              </p>
                              <p className="text-xs font-mono text-green-500/60">
                                {processSuccess.transactionHash}
                              </p>
                            </div>
                            <button
                              onClick={() => setProcessSuccess(null)}
                              className="text-green-500/60 hover:text-green-500"
                            >
                              ×
                            </button>
                          </div>
                        </CardContent>
                      </Card>
                    )}

                    {/* Process Error Message */}
                    {processError && (
                      <Card className="bg-red-500/10 border border-red-500/20">
                        <CardContent className="p-4">
                          <div className="flex items-start gap-3">
                            <RiErrorWarningLine className="size-5 text-red-500 flex-shrink-0 mt-0.5" />
                            <div className="flex-1">
                              <h4 className="text-sm font-semibold text-red-500 mb-1">Processing Failed</h4>
                              <p className="text-xs text-red-500/80">{processError}</p>
                            </div>
                            <button
                              onClick={() => setProcessError("")}
                              className="text-red-500/60 hover:text-red-500"
                            >
                              ×
                            </button>
                          </div>
                        </CardContent>
                      </Card>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Relay Fee Warning Modal */}
              {showRelayFeeWarning && relayFeeError && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
                  <Card className="bg-sidebar border border-[#FF8A3C] max-w-2xl w-full max-h-[90vh] overflow-auto">
                    <CardHeader>
                      <div className="flex items-start gap-4">
                        <RiAlertLine className="size-8 text-yellow-500 flex-shrink-0 mt-1" />
                        <div>
                          <CardTitle className="text-xl text-yellow-500">Relay Address Needs Funding</CardTitle>
                          <CardDescription className="mt-2 text-base">
                            {relayFeeError}
                          </CardDescription>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-6">
                      {/* Relay Address Info */}
                      <div className="space-y-3">
                        <div className="text-sm font-medium text-foreground">
                          Relay Address on {toNetwork.name}:
                        </div>
                        <div className="flex items-center gap-2 p-3 bg-background rounded-lg border border-border">
                          <code className="flex-1 text-sm font-mono break-all">{toNetwork.relay}</code>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              navigator.clipboard.writeText(toNetwork.relay);
                            }}
                            className="flex-shrink-0"
                          >
                            <RiFileCopyLine className="size-4" />
                          </Button>
                        </div>
                      </div>

                      {/* QR Code */}
                      <div className="flex flex-col items-center gap-3 p-6 bg-white rounded-lg border border-border">
                        <QRCodeSVG
                          value={toNetwork.relay}
                          size={200}
                          level="H"
                          includeMargin={true}
                          bgColor="#ffffff"
                          fgColor="#000000"
                        />
                        <p className="text-sm text-gray-700 text-center font-medium">
                          Scan this address with your mobile wallet to send {toNetwork.nativeTokenSymbol}
                        </p>
                        <p className="text-xs text-gray-500 text-center font-mono break-all px-4">
                          {toNetwork.relay}
                        </p>
                      </div>

                      {/* Block Explorer Link */}
                      <div className="flex items-center justify-center">
                        <a
                          href={`${toNetwork.scanURL}/address/${toNetwork.relay}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 text-[#FF8A3C] hover:text-[#FF8A3C]/80 transition-colors"
                        >
                          <span>View on {toNetwork.name} Explorer</span>
                          <RiExternalLinkLine className="size-4" />
                        </a>
                      </div>

                      {/* Warning and Confirmation */}
                      <div className="space-y-4 p-4 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
                        <div className="flex items-start gap-3">
                          <RiAlertLine className="size-5 text-yellow-500 flex-shrink-0 mt-0.5" />
                          <div className="text-sm text-yellow-500">
                            <p className="font-medium">Important Notice</p>
                            <p className="text-yellow-400 mt-1">
                              Your bridge transaction may be delayed or fail to complete on the destination chain
                              if the relay address doesn&#39;t have sufficient {toNetwork.nativeTokenSymbol} for gas fees.
                            </p>
                          </div>
                        </div>

                        <label className="flex items-start gap-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={proceedDespiteWarning}
                            onChange={(e) => setProceedDespiteWarning(e.target.checked)}
                            className="mt-1 size-4 rounded border-gray-300 text-[#FF8A3C] focus:ring-[#FF8A3C]"
                          />
                          <span className="text-sm text-foreground">
                            I understand the risks and want to proceed anyway
                          </span>
                        </label>
                      </div>

                      {/* Action Buttons */}
                      <div className="flex gap-3">
                        <Button
                          variant="outline"
                          className="flex-1"
                          onClick={() => {
                            setShowRelayFeeWarning(false);
                            setProceedDespiteWarning(false);
                          }}
                        >
                          Cancel
                        </Button>
                        <Button
                          className="flex-1 bg-[#FF8A3C] text-white hover:bg-[#FF8A3C]/90"
                          onClick={() => {
                            setShowRelayFeeWarning(false);
                            if (proceedDespiteWarning) {
                              handleBridge();
                            }
                          }}
                          disabled={!proceedDespiteWarning}
                        >
                          Proceed with Bridge
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {/* Bridge Card */}
                <Card className="bg-sidebar border border-border md:col-span-2">
                  <CardHeader>
                    <CardTitle>Bridge Assets</CardTitle>
                    <CardDescription>Transfer tokens between networks</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-6">
                      {/* Network Selection */}
                      <div className="flex flex-col md:flex-row gap-4 items-center">
                        <div className="flex-1">
                          <div className="text-sm text-muted-foreground mb-2">From Network</div>
                          <div className="relative">
                            <select
                              value={fromNetwork.id}
                              onChange={(e) => {
                                const network = BRIDGE_NETWORKS.find(n => n.id === parseInt(e.target.value));
                                if (network && network.id !== toNetwork.id) {
                                  setFromNetwork(network);
                                }
                              }}
                              className="w-full h-12 rounded-md border border-input bg-background px-3 py-2 text-sm appearance-none cursor-pointer"
                            >
                              {BRIDGE_NETWORKS.map((network) => (
                                <option
                                  key={network.id}
                                  value={network.id}
                                  disabled={network.id === toNetwork.id}
                                >
                                  {network.name}
                                </option>
                              ))}
                            </select>
                            <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                              </svg>
                            </div>
                          </div>
                        </div>

                        <div className="flex items-center justify-center">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-10 w-10 rounded-full border border-border bg-sidebar"
                            onClick={switchNetworks}
                          >
                            <RiArrowLeftRightLine className="size-5" />
                          </Button>
                        </div>

                        <div className="flex-1">
                          <div className="text-sm text-muted-foreground mb-2">To Network</div>
                          <div className="relative">
                            <select
                              value={toNetwork.id}
                              onChange={(e) => {
                                const network = BRIDGE_NETWORKS.find(n => n.id === parseInt(e.target.value));
                                if (network && network.id !== fromNetwork.id) {
                                  setToNetwork(network);
                                }
                              }}
                              className="w-full h-12 rounded-md border border-input bg-background px-3 py-2 text-sm appearance-none cursor-pointer"
                            >
                              {BRIDGE_NETWORKS.map((network) => (
                                <option
                                  key={network.id}
                                  value={network.id}
                                  disabled={network.id === fromNetwork.id}
                                >
                                  {network.name}
                                </option>
                              ))}
                            </select>
                            <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                              </svg>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Token Selection - List Style */}
                      {availableTokens.length > 0 ? (
                        <div>
                          <div className="text-sm text-muted-foreground mb-3">Select Token</div>
                          <div className="space-y-2">
                            {availableTokens.map((token) => (
                              <button
                                key={token.address}
                                onClick={() => setSelectedToken(token)}
                                className={`w-full p-3 rounded-lg border transition-all ${
                                  selectedToken?.address === token.address
                                    ? 'border-[#FF8A3C] bg-[#FF8A3C]/10'
                                    : 'border-border bg-background hover:border-[#FF8A3C]/50'
                                }`}
                              >
                                <div className="flex items-center gap-3">
                                  <img
                                    src={token.logoURI}
                                    alt={token.symbol}
                                    width={32}
                                    height={32}
                                    className="rounded-full"
                                  />
                                  <div className="flex-1 text-left">
                                    <div className="font-medium">{token.symbol}</div>
                                    <div className="text-xs text-muted-foreground">{token.name}</div>
                                  </div>
                                  {selectedToken?.address === token.address && (
                                    <RiCheckLine className="size-5 text-[#FF8A3C]" />
                                  )}
                                </div>
                              </button>
                            ))}
                          </div>
                        </div>
                      ) : (
                        <Card className="bg-yellow-500/10 border border-yellow-500/20">
                          <CardContent className="p-4">
                            <p className="text-sm text-yellow-500">
                              No common tokens available between {fromNetwork.name} and {toNetwork.name}
                            </p>
                          </CardContent>
                        </Card>
                      )}

                      {/* Token Amount */}
                      {selectedToken && (
                        <div className="p-4 bg-background rounded-lg border border-border">
                          <div className="flex justify-between items-center mb-2">
                            <span className="text-sm text-muted-foreground">Amount</span>
                            <span className="text-sm text-muted-foreground">
                              Balance: {formatBalance(tokenBalance as bigint, selectedToken.decimals)} {selectedToken.symbol}
                            </span>
                          </div>
                          <div className="flex gap-2 items-center">
                            <Input
                              type="number"
                              placeholder="0.0"
                              value={amount}
                              onChange={(e) => setAmount(e.target.value)}
                              className="bg-transparent border-none text-2xl font-medium focus-visible:ring-0 focus-visible:ring-offset-0 p-0 h-auto flex-1"
                            />
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                if (tokenBalance) {
                                  setAmount(formatBalance(tokenBalance as bigint, selectedToken.decimals));
                                }
                              }}
                              disabled={!tokenBalance}
                            >
                              MAX
                            </Button>
                          </div>
                        </div>
                      )}

                      {/* Bridge Info */}
                      {selectedToken && amount && (
                        <div className="space-y-2 text-sm p-4 bg-background rounded-lg border border-border">
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Estimated Time</span>
                            <span>~{fromNetwork.blockConfirmations * 2}-{fromNetwork.blockConfirmations * 3} minutes</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">You will receive</span>
                            <span className="font-medium">{amount} {selectedToken.symbol}</span>
                          </div>
                        </div>
                      )}

                      {/* Approval Warning */}
                      {selectedToken && needsApproval() && amount && (
                        <div className="flex items-start gap-2 p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg">
                          <RiAlertLine className="size-5 text-blue-500 mt-0.5 flex-shrink-0" />
                          <div className="text-sm text-blue-500">
                            <p className="font-medium">Approval Required</p>
                            <p className="text-blue-400 mt-1">
                              You need to approve the bridge contract to spend your {selectedToken.symbol} tokens before bridging.
                            </p>
                          </div>
                        </div>
                      )}

                      {/* Bridge Buttons */}
                      <div className="space-y-3">
                        {selectedToken && needsApproval() && (
                          <Button
                            className="w-full bg-blue-600 text-white hover:bg-blue-700"
                            onClick={handleApprove}
                            disabled={!isConnected || !isValidAmount() || isApproving || isApprovePending}
                          >
                            {isApproving || isApprovePending ? (
                              <>
                                <RiLoader4Line className="size-5 mr-2 animate-spin" />
                                Approving...
                              </>
                            ) : (
                              <>
                                <RiCheckLine className="size-5 mr-2" />
                                Approve {selectedToken.symbol}
                              </>
                            )}
                          </Button>
                        )}

                        <Button
                          className="w-full bg-[#FF8A3C] text-white hover:bg-[#FF8A3C]/90"
                          onClick={handleBridge}
                          disabled={!isConnected || !selectedToken || !isValidAmount() || needsApproval() || isBridging || isBridgePending}
                        >
                          {isBridging || isBridgePending ? (
                            <>
                              <RiLoader4Line className="size-5 mr-2 animate-spin" />
                              Bridging...
                            </>
                          ) : (
                            <>
                              <RiExchangeFundsLine className="size-5 mr-2" />
                              Bridge Tokens
                            </>
                          )}
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Transaction History Card */}
                <Card className="bg-sidebar border border-border h-fit max-h-[800px] flex flex-col">
                  <CardHeader className="flex flex-row items-center justify-between">
                    <div className="flex items-center gap-2">
                      <RiHistoryLine className="size-5 text-[#FF8A3C]" />
                      <CardTitle>Transaction History</CardTitle>
                    </div>
                    {isLoadingHistory && (
                      <RiLoader4Line className="size-4 animate-spin text-muted-foreground" />
                    )}
                  </CardHeader>
                  <CardContent className="flex-1 overflow-auto">
                    {!isConnected ? (
                      <div className="flex flex-col items-center justify-center py-12 text-center">
                        <RiAlertLine className="size-12 text-muted-foreground mb-4" />
                        <p className="text-sm text-muted-foreground">
                          Connect your wallet to view transaction history
                        </p>
                      </div>
                    ) : historyError ? (
                      <div className="flex flex-col items-center justify-center py-12 text-center">
                        <RiErrorWarningLine className="size-12 text-red-500 mb-4" />
                        <p className="text-sm text-red-500">{historyError}</p>
                      </div>
                    ) : transactions.length === 0 ? (
                      <div className="flex flex-col items-center justify-center py-12 text-center">
                        <RiHistoryLine className="size-12 text-muted-foreground mb-4" />
                        <p className="text-sm text-muted-foreground">
                          No bridge transactions yet
                        </p>
                      </div>
                    ) : (
                      <>
                        <div className="space-y-3">
                          {(() => {
                            // Calculate pagination
                            const startIndex = (currentPage - 1) * transactionsPerPage;
                            const endIndex = startIndex + transactionsPerPage;
                            const currentTransactions = transactions.slice(startIndex, endIndex);

                            return currentTransactions.map((tx) => (
                              <div
                                key={tx.id || tx.txHash}
                                className="p-4 rounded-lg border border-border bg-background hover:border-[#FF8A3C]/50 transition-colors"
                              >
                                <div className="flex items-start justify-between mb-2">
                                  <div className="flex items-center gap-2">
                                    <span className={`flex items-center gap-1 text-xs font-medium ${getStatusColor(tx.status)}`}>
                                      {getStatusIcon(tx.status)}
                                      <span className="capitalize">{tx.status || 'Unknown'}</span>
                                    </span>
                                  </div>
                                  {tx.txHash && (
                                    <div className="flex gap-1">
                                      <a
                                        href={getExplorerUrl(tx.fromChainId, tx.txHash)}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-[#FF8A3C] hover:text-[#FF8A3C]/80 transition-colors"
                                        title="View source transaction"
                                      >
                                        <RiExternalLinkLine className="size-4" />
                                      </a>
                                      {tx.destinationHash && tx.destinationHash !== '0x' && (
                                        <a
                                          href={getExplorerUrl(tx.toChainId, tx.destinationHash)}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="text-green-500 hover:text-green-400 transition-colors"
                                          title="View destination transaction"
                                        >
                                          <RiCheckLine className="size-4" />
                                        </a>
                                      )}
                                    </div>
                                  )}
                                </div>

                                <div className="space-y-1.5 text-sm">
                                  <div className="flex items-center gap-2 text-muted-foreground">
                                    <span className="font-medium text-foreground">{tx.fromChainName || 'Unknown'}</span>
                                    <RiArrowLeftRightLine className="size-3" />
                                    <span className="font-medium text-foreground">{tx.toChainName || 'Unknown'}</span>
                                  </div>

                                  <div className="text-foreground font-semibold text-base">
                                    {parseFloat(tx.amount || '0').toFixed(4)} {tx.tokenSymbol || 'tokens'}
                                  </div>

                                  <div className="text-xs text-muted-foreground">
                                    {formatTimestamp(tx.timestamp)}
                                  </div>

                                  {tx.txHash && (
                                    <div className="space-y-0.5">
                                      <div className="text-xs text-muted-foreground">
                                        Source:
                                        <span className="font-mono ml-1">
                                          {tx.txHash.slice(0, 8)}...{tx.txHash.slice(-6)}
                                        </span>
                                      </div>
                                      {tx.destinationHash && tx.destinationHash !== '0x' && (
                                        <div className="text-xs text-muted-foreground">
                                          Dest:
                                          <span className="font-mono ml-1">
                                            {tx.destinationHash.slice(0, 8)}...{tx.destinationHash.slice(-6)}
                                          </span>
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>
                              </div>
                            ));
                          })()}
                        </div>

                        {/* Pagination Controls */}
                        {transactions.length > transactionsPerPage && (
                          <div className="mt-6 pt-4 border-t border-border">
                            <div className="flex items-center justify-between">
                              <div className="text-sm text-muted-foreground">
                                Page {currentPage} of {Math.ceil(transactions.length / transactionsPerPage)}
                              </div>

                              <div className="flex items-center gap-2">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                                  disabled={currentPage === 1}
                                  className="border-[#FF8A3C]/30 hover:bg-[#FF8A3C]/10 hover:border-[#FF8A3C] disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                  Previous
                                </Button>

                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => setCurrentPage(prev => Math.min(Math.ceil(transactions.length / transactionsPerPage), prev + 1))}
                                  disabled={currentPage === Math.ceil(transactions.length / transactionsPerPage)}
                                  className="border-[#FF8A3C]/30 hover:bg-[#FF8A3C]/10 hover:border-[#FF8A3C] disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                  Next
                                </Button>
                              </div>
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* About Bridge Section - Moved to Bottom */}
              <Card className="bg-sidebar border border-border mt-8">
                <CardHeader>
                  <CardTitle>About Bridge</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-sm text-muted-foreground">
                    <div>
                      <p className="mb-4">
                        Bridge your assets securely between Larissa and other blockchain networks.
                      </p>
                      <p>
                        Our bridge uses a lock-and-mint mechanism to ensure your assets are safely transferred across chains.
                      </p>
                    </div>

                    <div>
                      <h4 className="font-medium text-foreground mb-3">Supported Networks</h4>
                      <ul className="space-y-2">
                        <li>• Larissa (LRS)</li>
                        <li>• Ethereum (ETH)</li>
                        <li>• Polygon (MATIC)</li>
                        <li>• Binance Smart Chain (BNB)</li>
                      </ul>
                      <h4 className="font-medium text-foreground mb-2 mt-4">Supported Tokens</h4>
                      <ul className="space-y-2">
                        <li>• LRS (Larissa)</li>
                        <li>• WLRS (Wrapped LRS)</li>
                        <li>• USDT (Tether USD)</li>
                        <li>• USDC (USD Coin)</li>
                      </ul>
                    </div>

                    <div>
                      <h4 className="font-medium text-foreground mb-2">Important Notes</h4>
                      <ul className="space-y-2">
                        <li>• Minimum bridge amount varies by token</li>
                        <li>• Bridge fee is paid in the source network&#39;s native token</li>
                        <li>• Transactions require {fromNetwork.blockConfirmations} block confirmations</li>
                        <li>• Always verify the destination address</li>
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </SidebarInset>
    </SidebarProvider>
  );
}

